import { MessageResponseWithBlocks } from "@/components/ai-elements/message-response-with-blocks";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { hasStreamCache, useAIMessageStream } from "@/hooks/useAIMessageStream";
import { useChatActionEmitter } from "@/hooks/useChatActions";
import { ReloadIcon } from "@radix-ui/react-icons";
import { CircleStopIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { RiRobot2Line } from "react-icons/ri";
import { TbCircleFilled } from "react-icons/tb";
import PageSkeleton from "./page-skeleton";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export default function CardAIStream({
  title,
  streamUrl,
  launchFetch = true,
  showAvatar = true,
  onTextChange,
  truncated = false,
  onCardClick,
  hideSuggestions = false,
}: {
  title: string;
  streamUrl: string;
  launchFetch?: boolean;
  showAvatar?: boolean;
  onTextChange?: (text: string, isComplete: boolean) => void;
  truncated?: boolean;
  onCardClick?: () => void;
  hideSuggestions?: boolean;
}) {
  const { t } = useTranslation();
  const { sendChatMessage, isPending: isChatPending } = useChatActionEmitter();

  const [isPreLoading, setIsPreLoading] = useState(() => !hasStreamCache(streamUrl));

  const { assistantMessage, status, refusal, fetchData, stop } = useAIMessageStream({
    streamUrl,
    launchFetch,
    showsRefusal: true,
    onStart: () => {
      setIsPreLoading(false);
    },
  });

  // Extract text content from message parts
  const textContent = useMemo(() => {
    if (!assistantMessage?.parts) return "";

    return assistantMessage.parts
      .filter((part) => part.type === "text")
      .map((part) => (part as { type: "text"; text: string }).text)
      .join("");
  }, [assistantMessage?.parts]);

  // Strip internal tags (e.g. [@shaarp/forecast ...]) before display
  const displayTextContent = useMemo(
    () => textContent.replace(/\[@shaarp\/forecast\s+[^\]]*\]/g, "").trim(),
    [textContent],
  );

  useEffect(() => {
    onTextChange?.(textContent, status === "ready");
  }, [textContent, status, onTextChange]);

  // Extract reasoning parts
  const reasoningParts = useMemo(() => {
    if (!assistantMessage?.parts) return [];

    return assistantMessage.parts.filter((part) => part.type === "reasoning");
  }, [assistantMessage?.parts]);

  const handleSuggestionClick = (prompt: string) => {
    if (!isChatPending) {
      sendChatMessage(prompt);
    }
  };

  // Smooth height animation
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const updateHeight = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    outer.style.height = `${inner.scrollHeight}px`;
  }, []);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const observer = new ResizeObserver(updateHeight);
    observer.observe(inner);

    return () => observer.disconnect();
  }, [updateHeight]);

  const isAnimating = isPreLoading || status === "streaming";

  const handleCardKeyDown = onCardClick
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick();
        }
      }
    : undefined;

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-[14px] p-[2px] transition-all duration-300",
        isAnimating && "shadow-2xl shadow-purple-500/30",
        onCardClick && "cursor-pointer",
      )}
      onClick={onCardClick}
      onKeyDown={handleCardKeyDown}
      role={onCardClick ? "button" : undefined}
      tabIndex={onCardClick ? 0 : undefined}
    >
      <span
        className={cn(
          "absolute inset-[-1000%] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]",
          isAnimating && "animate-[spin_2s_linear_infinite]",
        )}
      />
      <Card className="relative w-full border-0">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="shrink-0 rounded-full flex items-center justify-center w-10 h-10 bg-muted">
              <RiRobot2Line className="w-5 h-5" />
            </div>
            <span className="text-lg">{title}</span>
          </div>

          {truncated ? null : status === "streaming" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="secondary"
                  className="ml-auto gap-1.5 text-destructive"
                  onClick={() => stop()}
                >
                  <CircleStopIcon className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("components.cardContentAIStream.stopTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fetchData({ regenerate: true })}
                  className="ml-2"
                  disabled={isPreLoading}
                >
                  <ReloadIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("components.cardContentAIStream.regenerateTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className={cn(truncated && "relative")}>
        <div
          ref={outerRef}
          className={cn(
            "overflow-hidden transition-[height] duration-300 ease-in-out",
            truncated && "max-h-[260px]",
          )}
        >
          <div ref={innerRef} className={cn(truncated && "pointer-events-none")}>
            {isPreLoading ? (
              <PageSkeleton />
            ) : (
              <>
                {status !== "streaming" &&
                  status !== "submitted" &&
                  !assistantMessage && (
                    <>
                      {/* A refusal says why: a spent daily budget is not a
                          failure a retry fixes today. */}
                      <p className="text-center mb-[15px]">
                        {t(
                          refusal ?? "components.cardContentAIStream.notAvailable",
                        )}
                      </p>
                      <div className="text-center">
                        <Button onClick={() => fetchData({ regenerate: true })}>
                          <ReloadIcon className="mr-2" />
                          {t("components.cardContentAIStream.regenerate")}
                        </Button>
                      </div>
                    </>
                  )}
                {assistantMessage && (
                  <div className="flex flex-1 gap-4 items-start">
                    {showAvatar && (
                      <RiRobot2Line className="p-2 w-9 h-9 rounded-full bg-primary text-secondary border-primary shrink-0" />
                    )}
                    <div className="flex-1 py-1 w-full">
                      {/* Reasoning parts */}
                      {reasoningParts.map((part, i) => (
                        <Reasoning
                          key={`reasoning-${i}`}
                          className="w-full"
                          isStreaming={
                            status === "streaming" &&
                            i === reasoningParts.length - 1
                          }
                        >
                          <ReasoningTrigger />
                          <ReasoningContent>
                            {(part as { type: "reasoning"; text: string }).text}
                          </ReasoningContent>
                        </Reasoning>
                      ))}

                      {/* Render text with inline suggestions and email blocks (works during streaming too) */}
                      {displayTextContent && (
                        <>
                          <MessageResponseWithBlocks
                            onSuggestionClick={handleSuggestionClick}
                            disabled={isChatPending}
                            isStreaming={status === "streaming"}
                            hideSuggestions={hideSuggestions}
                          >
                            {displayTextContent}
                          </MessageResponseWithBlocks>
                          {status === "streaming" && (
                            <TbCircleFilled className="animate-pulse" size={20} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {truncated && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-[12px] bg-gradient-to-t from-card via-card/80 to-transparent" />
            {onCardClick && (
              <div className="absolute bottom-3 right-3">
                <Button
                  variant="outline"
                  className="h-10 px-4 rounded-full shadow-sm active:brightness-90"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCardClick();
                  }}
                >
                  <span className="text-sm">
                    {t("components.cardContentAIStream.seeMore")}
                  </span>
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
